import React, { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import SectionHeading from "./SectionHeading";
import PmsWaitlistModal from "./PmsWaitlistModal";
import PrivateWealthCardSkeleton from "./PrivateWealthCardSkeleton";
import PendingActionBadge from "./PendingActionBadge";
import { PRIVATE_WEALTH_PRODUCTS } from "./constants/productRouteMap";
import {
  hasProductPendingCall,
  getProductPendingCount,
} from "./utils/pendingCall";

const THEME_STYLES = {
  gold: {
    bg: "linear-gradient(121deg, #F9E8C0 1%, #F0D899 99%)",
    cardBg: "linear-gradient(180deg, #7E5702 0%, #402C02 100%)",
    border: "1px solid rgba(62,43,3,0.4)",
    ctaBg: "#7C5602",
    titleColor: "#3E2B03",
  },
  blue: {
    cardBg: "linear-gradient(180deg, #4E5E8D 0%, #161B2B 100%)",
    border: "0.75px solid rgba(65,134,240,0.6)",
    ctaBg: "rgba(255,255,255,0.15)",
  },
  navy: {
    cardBg: "linear-gradient(180deg, #1565C0 0%, #072A62 100%)",
    border: "1px solid rgba(65,134,240,0.6)",
    ctaBg: "rgba(255,255,255,0.15)",
  },
  teal: {
    cardBg: "linear-gradient(180deg, #027294 0%, #002C39 100%)",
    border: "1px solid rgba(2,112,145,0.4)",
    ctaBg: "rgba(255,255,255,0.15)",
  },
  bronze: {
    cardBg: "linear-gradient(180deg, #C06A26 0%, #1A120B 100%)",
    border: "1px solid rgba(192,106,38,0.5)",
    ctaBg: "rgba(255,255,255,0.15)",
  },
};

const TAB_TO_SECTION = {
  portfolioManagement: "portfolio_management",
  derivatives: "derivatives",
};

const PW_ICON = (name) =>
  `/images/product-info/private-wealth/${encodeURIComponent(name)}`;

const MARKETING_THEMES = {
  prime: {
    bg: "#FEFAF6",
    border: "#C89214",
    titleColor: "#946B09",
    subtitleColor: "#010208",
    subtitle: "Hyper-Personalized Portfolio Management",
    subtitleMaxWidth: 154,
    ctaBg: "linear-gradient(90deg, #A88234 0%, #725719 100%)",
    ctaBorder: "#BE9131",
    ctaShadow: "0 6px 8px rgba(219,156,11,0.2)",
    iconBg: "#FFF5EB",
    benefitColor: "#765A1B",
    decor: PW_ICON("decor-prime-bg.svg"),
    // Full-bleed wave clip from Figma Clip path group
    decorSx: {
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "right top",
      opacity: 1,
    },
    // Figma 8684:90552 — left 137/302, top 92, 168×154
    hero: { left: "45.4%", top: 92, width: "55.6%", height: 154 },
    benefits: [
      { icon: PW_ICON("shield-check.svg"), label: "Expert Portfolio Management" },
      { icon: PW_ICON("target.svg"), label: "Tailored for High Net Worth Investors" },
      { icon: PW_ICON("chart-spline.svg"), label: "Long Term Wealth Creation" },
    ],
  },
  usStrategy: {
    bg: "#FCFCFC",
    border: "#0D5BBE",
    titleColor: "#0342A1",
    subtitleColor: "#010208",
    subtitle: "Professionally Managed US Multi-Asset Allocation",
    subtitleMaxWidth: 154,
    ctaBg: "linear-gradient(90deg, #4A82D0 0%, #0542A3 100%)",
    ctaBorder: "rgba(59,130,246,0.62)",
    ctaShadow: "0 6px 8px rgba(37,99,235,0.3)",
    iconBg: "#F0F6FC",
    benefitColor: "#0845A5",
    decor: PW_ICON("decor-us-strategy-bg.svg"),
    // Figma 8684:91400 — oversized + rotated wave graphic
    decorSx: {
      left: "-30%",
      top: "-23%",
      width: "177%",
      height: "197%",
      objectFit: "contain",
      transform: "rotate(-155.9deg) scaleY(-1)",
      transformOrigin: "center center",
      opacity: 0.9,
    },
    hero: { left: "43.8%", top: 79, width: "59%", height: 185 },
    benefits: [
      {
        icon: PW_ICON("globe.svg"),
        label: "Exposure to equities, commodities & fixed income",
      },
      { icon: PW_ICON("shield-check-blue.svg"), label: "Automated Execution" },
      {
        icon: PW_ICON("chart-pie-blue.svg"),
        label: "Powered by Proprietary AI Research",
      },
    ],
  },
  atlas: {
    bg: "#FFF1ED",
    border: "#E5803E",
    titleColor: "#631A00",
    subtitleColor: "rgba(0,0,0,0.8)",
    subtitle: "Recommendations across 10+ Global Markets",
    subtitleMaxWidth: 154,
    subtitleFontSize: 13.5,
    ctaBg: "linear-gradient(90deg, #EF8742 0%, #581A01 100%)",
    ctaBorder: "#9D4C1F",
    ctaShadow: "0 6px 8px rgba(157,76,31,0.3)",
    iconBg: "rgba(161,52,12,0.2)",
    benefitColor: "#692608",
    decor: PW_ICON("decor-atlas-bg.svg"),
    // Figma 8684:91462 — left -168.64, top -227.47, size ~806
    decorSx: {
      left: "-56%",
      top: "-73%",
      width: "267%",
      height: "267%",
      objectFit: "contain",
      transform: "rotate(-155.9deg) scaleY(-1)",
      transformOrigin: "center center",
      opacity: 0.95,
    },
    hero: {
      left: "41.7%",
      top: 60,
      width: "58.3%",
      height: 196,
      imgHeight: "135%",
      imgTop: "-17.5%",
    },
    benefits: [
      { icon: PW_ICON("chart-network.svg"), label: "Access 10+ Global Markets" },
      { icon: PW_ICON("chart-line.svg"), label: "Trade Equities & Commodities" },
      {
        icon: PW_ICON("chart-pie-bronze.svg"),
        label: "High-Conviction Trade Ideas",
      },
    ],
  },
  sentimentTracker: {
    bg: "linear-gradient(180deg, #F4F7FB 0%, #EEF2F7 55%, #E8EEF6 100%)",
    border: "rgba(65,134,240,0.55)",
    titleColor: "#10284D",
    subtitleColor: "#1A2B44",
    subtitle: "See What the Market is About to do",
    subtitleMaxWidth: 154,
    ctaBg: "linear-gradient(90deg, #1E4F9B 0%, #0B2F6B 100%)",
    ctaBorder: "rgba(30,79,155,0.7)",
    ctaShadow: "0 6px 8px rgba(15,55,120,0.28)",
    iconBg: "#E8EEF8",
    benefitColor: "#1A3A6B",
    hero: { left: "40%", top: 48, width: "60%", height: 200 },
    benefits: [
      {
        icon: "/images/sentiment-product-info/tracking%201.svg",
        label: "Decodes Institutional Positions",
      },
      {
        icon: "/images/sentiment-product-info/clock%202.svg",
        label: "Real-Time Derivatives Data",
      },
      {
        icon: "/images/sentiment-product-info/dashboard%201.svg",
        label: "Anticipate Moves Not Everyone Sees",
      },
    ],
  },
};

const PORTFOLIO_ORDER = ["prime", "usStrategy", "atlas"];

const COMING_SOON_PRODUCTS = PRIVATE_WEALTH_PRODUCTS.filter(
  (product) => product.comingSoon && !product.hidden,
);

const PORTFOLIO_STATIC_PRODUCTS = PRIVATE_WEALTH_PRODUCTS.filter(
  (product) =>
    !product.hidden &&
    (product.comingSoon || product.forceInPortfolioTab) &&
    product.sectionTab === "portfolio_management",
);

const PILL_SX = {
  gain: {
    color: "#00E676",
    bgcolor: "rgba(0,230,118,0.1)",
    border: "0.62px solid #00E676",
  },
  loss: {
    color: "#FF6B6B",
    bgcolor: "rgba(255,107,107,0.1)",
    border: "0.62px solid #FF6B6B",
  },
};

const DARK_PILL_SX = {
  gain: {
    color: "#00E676",
    bgcolor: "#132C33",
    border: "1px solid rgba(0,230,118,0.49)",
  },
  loss: {
    color: "#FF6B6B",
    bgcolor: "#2C1313",
    border: "1px solid rgba(255,107,107,0.49)",
  },
};

const GlassStat = ({ label, value, tone = "neutral", pillLabel, fullWidth }) => {
  const showPill =
    pillLabel && (tone === "gain" || tone === "loss");

  return (
    <Box
      sx={{
        flex: fullWidth ? "1 1 100%" : "1 1 calc(50% - 4px)",
        minWidth: fullWidth ? "100%" : "calc(50% - 4px)",
        minHeight: 54,
        px: 1,
        py: 1,
        borderRadius: "12px",
        border: "0.6px solid #393939",
        bgcolor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        boxShadow:
          "0px 1.25px 2.5px -1.25px rgba(0,0,0,0.15), 0px 2.5px 3.75px -0.6px rgba(0,0,0,0.08)",
        textAlign: "center",
        boxSizing: "border-box",
      }}
    >
      <Text
        sx={{
          color: "rgba(255,255,255,0.75)",
          fontSize: { xs: "0.6875rem", md: "0.625rem" },
          display: "block",
          mb: 0.5,
        }}
      >
        {label}
      </Text>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0.5}
        flexWrap="wrap"
      >
        <Text
          sx={{
            fontWeight: 700,
            color: "#fff",
            fontSize: "0.9375rem",
            lineHeight: 1,
          }}
        >
          {value ?? "—"}
        </Text>
        {showPill ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              px: 0.75,
              py: 0.35,
              borderRadius: "999px",
              fontSize: "0.6875rem",
              fontWeight: 700,
              ...DARK_PILL_SX[tone],
            }}
          >
            {pillLabel}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

const FrostedCta = ({ label, onClick, solidGold, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      mt: "auto",
      minHeight: 44,
      height: 44,
      borderRadius: "12px",
      border: solidGold
        ? "1px solid rgba(255,255,255,0.1)"
        : "0.75px solid rgba(255,255,255,0.25)",
      bgcolor: solidGold ? "#7C5602" : "rgba(255,255,255,0.15)",
      backdropFilter: solidGold ? "none" : "blur(9px)",
      boxShadow: solidGold
        ? "0px 10px 24px -8px rgba(124,86,2,0.35)"
        : "0px 7.5px 9px rgba(0,0,0,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      cursor: onClick && !disabled ? "pointer" : "default",
      opacity: disabled ? 0.55 : 1,
      "&:hover": onClick && !disabled ? { filter: "brightness(1.06)" } : {},
    }}
  >
    <Text sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#fff" }}>
      {label}
    </Text>
    <ArrowForwardIcon sx={{ fontSize: 15, color: "#fff" }} />
  </Box>
);

const ComingSoonCard = ({ product, onCtaClick }) => {
  
  const theme = THEME_STYLES[product.theme] || THEME_STYLES.navy;
  const ctaLabel = product.ctaLabel || "Join Waitlist";
  const isPms = product.id === "pms" || product.code === "PMS";

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "30px",
        background: theme.cardBg,
        border: theme.border,
        width: "100%",
        minWidth: 0,
        minHeight: 309,
        height: "100%",
        p: 2.25,
        color: "#fff",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        boxShadow:
          "0px 7.5px 18px -7.5px rgba(212,175,55,0.15), 0px 13.5px 30px -9px rgba(0,0,0,0.4)",
      }}
    >
      <Text
        sx={{
          fontWeight: 800,
          fontSize: "1.5rem",
          color: "#fff",
          lineHeight: 1.1,
        }}
      >
        {product.name}
      </Text>
      <Text
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1rem",
          fontWeight: 600,
          color: "rgba(255,255,255,0.85)",
        }}
      >
        Coming soon
      </Text>
      <FrostedCta label={ctaLabel} onClick={isPms ? onCtaClick : undefined} />
    </Box>
  );
};

const MarketingShowcaseCard = ({ product, onClick, ctaLabel = "Know More" }) => {
  const theme = MARKETING_THEMES[product.id] || MARKETING_THEMES.prime;
  const title = product.name?.replace(/^Jarvis\s+/i, "") || product.name;
  const subtitle = theme.subtitle || product.subtitle;
  const heroSrc =
    product.lockedHero ||
    (product.id === "prime"
      ? "/images/product-info/prime-locked-crown.png"
      : product.icon);
  const hero = theme.hero || { left: "44%", top: 80, width: "56%", height: 160 };
  const clickable = Boolean(onClick);
  // Cache-bust so stripped transparent heroes replace any black-studio cached PNGs
  const heroUrl = heroSrc
    ? `${heroSrc}${heroSrc.includes("?") ? "&" : "?"}v=20260723`
    : null;

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        position: "relative",
        borderRadius: "30px",
        ...(String(theme.bg).includes("gradient")
          ? { background: theme.bg }
          : { bgcolor: theme.bg }),
        border: `1px solid ${theme.border}`,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        minHeight: 309,
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 2.125 },
        pb: 1.25,
        cursor: clickable ? "pointer" : "default",
        boxShadow:
          "0px 7.5px 18px -7.5px rgba(200,146,20,0.15), 0px 0px 8px 0px rgba(0,0,0,0.24)",
        transition: "filter 0.2s",
        "&:hover": clickable ? { filter: "brightness(1.02)" } : {},
      }}
    >
      {theme.decor ? (
        <Box
          component="img"
          src={theme.decor}
          alt=""
          sx={{
            position: "absolute",
            pointerEvents: "none",
            zIndex: 0,
            ...(theme.decorSx || {
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "right top",
            }),
          }}
        />
      ) : null}

      {heroUrl ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: hero.left ?? "auto",
            right: hero.left ? "auto" : 0,
            top: { xs: Math.max(36, (hero.top || 80) - 20), sm: hero.top },
            width: { xs: "48%", sm: hero.width },
            height: { xs: 150, sm: hero.height },
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 1,
            opacity: { xs: 0.9, sm: 1 },
          }}
        >
          <Box
            component="img"
            src={heroUrl}
            alt=""
            sx={{
              width: "100%",
              height: hero.imgHeight || "100%",
              mt: hero.imgTop || 0,
              objectFit: "contain",
              objectPosition: "right center",
              display: "block",
            }}
          />
        </Box>
      ) : null}

      {/* Copy + CTA — flow layout so text never collides with the hero */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          pr: { xs: "40%", sm: "42%" },
          gap: 1,
        }}
      >
        <Text
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.25rem", sm: "1.375rem", md: "1.5rem" },
            lineHeight: 1.15,
            color: theme.titleColor,
            textShadow: "0 0 10px rgba(255,255,255,0.13)",
            fontFamily: "'DM Sans', sans-serif",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Text>

        {subtitle ? (
          <Text
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: "0.8125rem",
                sm: theme.subtitleFontSize
                  ? `${theme.subtitleFontSize / 16}rem`
                  : "0.875rem",
              },
              lineHeight: 1.4,
              color: theme.subtitleColor,
              maxWidth: "100%",
              fontFamily: "'DM Sans', sans-serif",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {subtitle}
          </Text>
        ) : null}

        <Box
          sx={{
            mt: "auto",
            mb: 1.25,
            alignSelf: "flex-start",
            minHeight: 28,
            height: 28,
            px: 1.5,
            borderRadius: "7.5px",
            background: theme.ctaBg,
            border: `0.75px solid ${theme.ctaBorder}`,
            boxShadow: theme.ctaShadow,
            backdropFilter: "blur(4.5px)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxSizing: "border-box",
          }}
        >
          <Text
            sx={{
              fontWeight: 600,
              fontSize: 10,
              color: "#E7F2FF",
              textShadow: "0 1px 4px rgba(0,0,0,0.4)",
              lineHeight: "12px",
              whiteSpace: "nowrap",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {ctaLabel}
          </Text>
          <ArrowForwardIcon sx={{ fontSize: 12, color: "#E7F2FF" }} />
        </Box>
      </Box>

      {/* Benefits — icon-above-label so 3 columns stay readable when narrow */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          width: "100%",
          minHeight: 76,
          px: { xs: 1, sm: 1.25 },
          py: 1,
          borderRadius: "20px",
          bgcolor: "rgba(255,255,255,0.55)",
          border: "1px solid #EDF2F7",
          backdropFilter: "blur(3px)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.02)",
          display: "flex",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        {theme.benefits.map((benefit, index) => (
          <React.Fragment key={benefit.label}>
            {index > 0 ? (
              <Box
                sx={{
                  width: "1px",
                  alignSelf: "stretch",
                  my: 0.5,
                  bgcolor: "rgba(0,0,0,0.1)",
                  flexShrink: 0,
                  mx: { xs: 0.5, sm: 0.75 },
                }}
              />
            ) : null}
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                textAlign: "center",
                px: 0.25,
              }}
            >
              <Box
                sx={{
                  width: { xs: 24, sm: 28 },
                  height: { xs: 24, sm: 28 },
                  flexShrink: 0,
                  borderRadius: "14px",
                  bgcolor: theme.iconBg,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Box
                  component="img"
                  src={benefit.icon}
                  alt=""
                  sx={{
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    display: "block",
                  }}
                />
              </Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  width: "100%",
                  fontSize: { xs: 9, sm: 10 },
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: theme.benefitColor,
                  fontFamily: "'DM Sans', sans-serif",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  hyphens: "auto",
                }}
              >
                {benefit.label}
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const UnlockedMetricCell = ({ label, value, labelColor, valueColor }) => (
  <Box
    sx={{
      flex: "1 1 0",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      textAlign: "center",
    }}
  >
    <Text
      sx={{
        fontWeight: 400,
        fontSize: 10,
        lineHeight: 1.3,
        letterSpacing: "-0.3px",
        color: labelColor,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {label}
    </Text>
    <Text
      sx={{
        fontWeight: 700,
        fontSize: { xs: 13, sm: 15 },
        lineHeight: 1,
        letterSpacing: "-0.45px",
        color: valueColor,
        fontFamily: "'DM Sans', sans-serif",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value ?? "—"}
    </Text>
  </Box>
);

const UnlockedMetricsTray = ({ stats, benefitColor, valueColor }) => {
  if (!stats?.length) return null;

  const topStats = stats.slice(0, 2);
  const returnsStat = stats[2] || null;
  const showPill =
    returnsStat?.pillLabel &&
    (returnsStat.tone === "gain" || returnsStat.tone === "loss");

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 3,
        width: "100%",
        minHeight: 142,
        px: 1.5,
        py: 1.75,
        borderRadius: "20px",
        bgcolor: "rgba(255,255,255,0.55)",
        border: "1px solid #EDF2F7",
        backdropFilter: "blur(3px)",
        boxShadow: "0 4px 8px rgba(0,0,0,0.02)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {topStats.map((stat, index) => (
          <React.Fragment key={`${stat.label}-${stat.value}`}>
            {index > 0 ? (
              <Box
                sx={{
                  width: "1px",
                  height: 40,
                  bgcolor: "rgba(0,0,0,0.12)",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <UnlockedMetricCell
              label={stat.label}
              value={stat.value}
              labelColor={benefitColor}
              valueColor={valueColor}
            />
          </React.Fragment>
        ))}
      </Box>

      {returnsStat ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "5px",
            width: "100%",
          }}
        >
          <Text
            sx={{
              fontWeight: 400,
              fontSize: 10,
              lineHeight: 1.3,
              letterSpacing: "-0.3px",
              color: benefitColor,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {returnsStat.label}
          </Text>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: "5px",
              flexWrap: "wrap",
              maxWidth: "100%",
            }}
          >
            <Text
              sx={{
                fontWeight: 700,
                fontSize: { xs: 13, sm: 15 },
                lineHeight: 1,
                letterSpacing: "-0.45px",
                color: valueColor,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {returnsStat.value ?? "—"}
            </Text>
            {showPill ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2.5px",
                  px: "4.7px",
                  py: "3.1px",
                  borderRadius: "999px",
                  ...PILL_SX[returnsStat.tone],
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 7,
                    lineHeight: 1,
                    color: "inherit",
                  }}
                >
                  {returnsStat.tone === "loss" ? "▼" : "▲"}
                </Box>
                <Text
                  sx={{
                    fontWeight: 700,
                    fontSize: 7.5,
                    lineHeight: 1,
                    color: "inherit",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  {String(returnsStat.pillLabel).replace(/^[▲▼]\s*/, "")}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

const PrivateWealthUnlockedCard = ({
  product,
  onClick,
  hasPendingCall = false,
  pendingCount = 1,
}) => {
  const marketingTheme = MARKETING_THEMES[product.id];
  const darkTheme = THEME_STYLES[product.theme] || THEME_STYLES.gold;
  const isPrime = product.theme === "gold" || product.id === "prime";
  const stats = product.stats || [];
  const title =
    product.name?.replace(/^Jarvis\s+/i, "") || product.name;

  // Figma after-purchase: light marketing shell + metrics tray (Prime and themed PW products)
  if (marketingTheme) {
    const iconSrc =
      product.icon ||
      product.lockedHero ||
      (isPrime ? "/images/product-info/prime-locked-crown.png" : null);
    const valueColor = isPrime ? "#B88A2F" : marketingTheme.titleColor;
    const benefitColor = marketingTheme.benefitColor || "#765A1B";

    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: "30px",
          ...(String(marketingTheme.bg).includes("gradient")
            ? { background: marketingTheme.bg }
            : { bgcolor: marketingTheme.bg }),
          border: `1px solid ${marketingTheme.border}`,
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          minHeight: 309,
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 2.125 },
          pb: 1.5,
          boxShadow:
            "0px 7.5px 18px -7.5px rgba(200,146,20,0.15), 0px 0px 8px 0px rgba(0,0,0,0.24)",
        }}
      >
        {marketingTheme.decor ? (
          <Box
            component="img"
            src={marketingTheme.decor}
            alt=""
            sx={{
              position: "absolute",
              pointerEvents: "none",
              zIndex: 0,
              ...(marketingTheme.decorSx || {
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "right top",
                opacity: 0.9,
              }),
            }}
          />
        ) : null}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ position: "relative", zIndex: 2, mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
            {iconSrc ? (
              <Box
                component="img"
                src={iconSrc}
                alt=""
                sx={{
                  width: 32,
                  height: 29,
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Text
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.375rem", sm: "1.625rem" },
                lineHeight: 1.1,
                color: marketingTheme.titleColor,
                textShadow: "0 0 10px rgba(255,255,255,0.13)",
                fontFamily: "'DM Sans', sans-serif",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Text>
          </Stack>

          {hasPendingCall ? (
            <PendingActionBadge
              actionCentrePath={product.actionCentrePath}
              pendingCount={pendingCount}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 0,
          }}
        >
          <UnlockedMetricsTray
            stats={stats}
            benefitColor={benefitColor}
            valueColor={valueColor}
          />
        </Box>

        <Box
          onClick={onClick}
          sx={{
            position: "relative",
            zIndex: 3,
            mt: 1.5,
            width: "100%",
            minHeight: 42,
            height: 42,
            borderRadius: "7.5px",
            background: marketingTheme.ctaBg,
            border: `0.75px solid ${marketingTheme.ctaBorder}`,
            boxShadow: marketingTheme.ctaShadow,
            backdropFilter: "blur(4.5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            cursor: onClick ? "pointer" : "default",
            boxSizing: "border-box",
            "&:hover": onClick ? { filter: "brightness(1.05)" } : {},
          }}
        >
          <Text
            sx={{
              fontWeight: 500,
              fontSize: 14,
              color: "#fff",
              lineHeight: 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            View Dashboard
          </Text>
          <ArrowForwardIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
      </Box>
    );
  }

  // Fallback dark unlocked card for products without a marketing theme
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "30px",
        background: darkTheme.cardBg,
        border: darkTheme.border,
        width: "100%",
        minWidth: 0,
        minHeight: 309,
        height: "100%",
        p: 2.25,
        color: "#fff",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        boxShadow:
          "0px 7.5px 18px -7.5px rgba(212,175,55,0.15), 0px 13.5px 30px -9px rgba(0,0,0,0.4)",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {product.icon ? (
            <Box
              component="img"
              src={product.icon}
              alt=""
              sx={{ width: 36, height: 36, objectFit: "contain" }}
            />
          ) : null}
          <Text sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#fff" }}>
            {title}
          </Text>
        </Stack>
        {hasPendingCall ? (
          <PendingActionBadge
            actionCentrePath={product.actionCentrePath}
            pendingCount={pendingCount}
          />
        ) : product.infoDescription ? (
          <Tooltip
            title={product.infoDescription}
            arrow
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(11px)",
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Box
                component="img"
                src="/images/product-info/alert-circle.svg"
                alt=""
                sx={{ width: 20, height: 20 }}
              />
            </Box>
          </Tooltip>
        ) : null}
      </Stack>

      {product.riskProfile ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <Box
            component="img"
            src="/images/product-info/risk-gauge.svg"
            alt=""
            sx={{ width: 19, height: 17 }}
          />
          <Text sx={{ fontSize: "1rem", fontWeight: 600, color: "#fff" }}>
            {product.riskProfile}
          </Text>
        </Stack>
      ) : null}

      {product.monitorLabel && !product.riskProfile ? (
        <Text
          sx={{
            mt: 2,
            fontSize: "1rem",
            fontWeight: 600,
            color: "#fff",
          }}
        >
          {product.monitorLabel}
        </Text>
      ) : null}

      {stats.length > 0 ? (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {stats.map((stat, index) => (
            <GlassStat
              key={`${stat.label}-${stat.value}`}
              label={stat.label}
              value={stat.value}
              tone={stat.tone}
              pillLabel={stat.pillLabel}
              fullWidth={
                isPrime && stats.length === 3 && index === stats.length - 1
              }
            />
          ))}
        </Box>
      ) : null}

      <FrostedCta
        label="View Dashboard"
        onClick={onClick}
        solidGold={isPrime}
      />
    </Box>
  );
};

const PrivateWealthLockedCard = ({ product, onClick }) => {
  if (MARKETING_THEMES[product.id]) {
    return <MarketingShowcaseCard product={product} onClick={onClick} />;
  }

  const theme = THEME_STYLES[product.theme] || THEME_STYLES.gold;
  const isPrime = product.id === "prime" || product.theme === "gold";
  const title =
    product.name?.replace(/^Jarvis\s+/i, "") || product.name;
  const body =
    product.description ||
    product.infoDescription ||
    "";
  const iconSrc = isPrime
    ? "/images/product-info/prime-locked-crown.png"
    : product.icon;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        borderRadius: "30px",
        background: theme.cardBg,
        border: theme.border,
        minHeight: 309,
        height: "100%",
        p: 2.25,
        color: "#fff",
        cursor: onClick ? "pointer" : "default",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow:
          "0px 7.5px 18px -7.5px rgba(212,175,55,0.15), 0px 13.5px 30px -9px rgba(0,0,0,0.4)",
        transition: "filter 0.2s",
        "&:hover": onClick ? { filter: "brightness(1.03)" } : {},
      }}
    >
      <Text
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          color: "#fff",
          lineHeight: 1.15,
          maxWidth: { xs: "78%", sm: "84%" },
          position: "relative",
          zIndex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title}
      </Text>

      {product.subtitle ? (
        <Text
          sx={{
            mt: 1.25,
            fontSize: { xs: "0.8125rem", sm: "0.84375rem" },
            fontWeight: 600,
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.4,
            pr: { xs: "22%", sm: "18%" },
            position: "relative",
            zIndex: 1,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {product.subtitle}
        </Text>
      ) : null}

      {body && body !== product.subtitle ? (
        <Text
          sx={{
            mt: 2,
            fontSize: { xs: "0.75rem", md: "0.65625rem" },
            fontWeight: 400,
            color: "rgba(255,255,255,0.7)",
            lineHeight: 1.5,
            maxWidth: { xs: "68%", md: "58%" },
            pr: 1,
            position: "relative",
            zIndex: 1,
            display: "-webkit-box",
            WebkitLineClamp: { xs: 3, md: 4 },
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {body}
        </Text>
      ) : null}

      {iconSrc ? (
        <Box
          component="img"
          src={iconSrc}
          alt=""
          sx={{
            position: "absolute",
            right: isPrime ? { xs: -56, sm: -48 } : { xs: -44, sm: -36 },
            top: isPrime ? { xs: 96, sm: 88 } : { xs: 80, sm: 72 },
            width: isPrime ? { xs: 180, sm: 220 } : { xs: 150, sm: 180 },
            height: isPrime ? { xs: 170, sm: 210 } : { xs: 165, sm: 200 },
            objectFit: "contain",
            opacity: { xs: 0.85, sm: 0.95 },
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ) : null}

      <FrostedCta label="Know More" />
    </Box>
  );
};

const PrivateWealthCard = ({
  product,
  onClick,
  onPmsWaitlistClick,
  hasPendingCall = false,
  pendingCount = 1,
}) => {
  // US Strategy + PMS always use the same marketing / coming-soon UI
  if (product.comingSoon) {
    if (MARKETING_THEMES[product.id]) {
      return (
        <MarketingShowcaseCard
          product={product}
          onClick={product.productInfoPath ? onClick : undefined}
        />
      );
    }
    return (
      <ComingSoonCard
        product={product}
        onCtaClick={onPmsWaitlistClick}
      />
    );
  }

  // Sentiment should retain the same marketing shell after purchase and only
  // switch the CTA copy once the product is unlocked.
  if (product.id === "sentimentTracker") {
    return (
      <MarketingShowcaseCard
        product={product}
        onClick={onClick}
        ctaLabel={product.isLocked ? "Know More" : "View Dashboard"}
      />
    );
  }

  // After purchase: unlocked card must win over marketing showcase
  if (!product.isLocked) {
    return (
      <PrivateWealthUnlockedCard
        product={product}
        onClick={onClick}
        hasPendingCall={hasPendingCall}
        pendingCount={pendingCount}
      />
    );
  }

  if (MARKETING_THEMES[product.id]) {
    return (
      <MarketingShowcaseCard
        product={product}
        onClick={onClick}
        ctaLabel="Know More"
      />
    );
  }

  return <PrivateWealthLockedCard product={product} onClick={onClick} />;
};

const PrivateWealthSection = ({
  products = [],
  disabledProductCodes = [],
  onProductClick,
  loading = false,
  pendingCallData,
}) => {
  const [tab, setTab] = useState("portfolioManagement");
  const [isPmsWaitlistOpen, setIsPmsWaitlistOpen] = useState(false);
  const sectionKey = TAB_TO_SECTION[tab];
  const disabledCodes = useMemo(
    () => new Set(disabledProductCodes),
    [disabledProductCodes],
  );

  const visibleComingSoonProducts = useMemo(
    () =>
      COMING_SOON_PRODUCTS.filter((product) => !disabledCodes.has(product.code)),
    [disabledCodes],
  );

  const displayProducts = useMemo(() => {
    const isHidden = (product) =>
      product.hidden || product.code === "PMS" || product.id === "pms";

    const fromApi = products.filter(
      (product) => product.sectionTab === sectionKey && !isHidden(product),
    );

    if (tab !== "portfolioManagement") {
      return fromApi;
    }

    const byKey = new Map();
    fromApi.forEach((product) => {
      byKey.set(product.id || product.code, product);
    });

    PORTFOLIO_STATIC_PRODUCTS.forEach((staticProduct) => {
      if (disabledCodes.has(staticProduct.code) || isHidden(staticProduct)) {
        return;
      }

      const existingKey = [...byKey.keys()].find((key) => {
        const item = byKey.get(key);
        return (
          item.code === staticProduct.code || item.id === staticProduct.id
        );
      });

      if (!existingKey) {
        byKey.set(staticProduct.id, {
          ...staticProduct,
          isLocked: true,
          sectionTab: "portfolio_management",
        });
        return;
      }

      const existing = byKey.get(existingKey);
      byKey.set(existingKey, {
        ...staticProduct,
        ...existing,
        lockedHero: existing.lockedHero || staticProduct.lockedHero,
        subtitle: existing.subtitle || staticProduct.subtitle,
        productInfoPath:
          existing.productInfoPath || staticProduct.productInfoPath,
      });
    });

    return [...byKey.values()].sort((a, b) => {
      const ai = PORTFOLIO_ORDER.indexOf(a.id);
      const bi = PORTFOLIO_ORDER.indexOf(b.id);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [products, sectionKey, tab, disabledCodes]);

  const hasPortfolioStatic = PORTFOLIO_STATIC_PRODUCTS.some(
    (product) => !disabledCodes.has(product.code) && !product.hidden,
  );

  if (
    !products.length &&
    !visibleComingSoonProducts.length &&
    !hasPortfolioStatic
  ) {
    return null;
  }

  // Only show skeletons on the true first load, never on background
  // refetches, so already-rendered cards don't flicker away.
  const showSkeleton = loading && !products.length;
  const SKELETON_COUNT = 3;
  const gridItemCount = showSkeleton ? SKELETON_COUNT : displayProducts.length;

  return (
    <Box
      sx={{
        borderRadius: "24px",
        background:
          "linear-gradient(115deg, #FFF2E5 1.4%, #FFFFFF 98.6%)",
        border: "1px solid #BE9131",
        p: { xs: 2, md: 3 },
        boxShadow: "0 0 4.5px rgba(0,0,0,0.12)",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ mb: 2.5, minWidth: 0, width: "100%" }}
      >
        <SectionHeading
          title="Private Wealth"
          iconSrc="/images/product-info/private-wealth/private-wealth.svg"
          titleColor="#000000"
        />
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            bgcolor: "rgba(255,255,255,0.8)",
            borderRadius: "16px",
            p: 0.5,
            border: "1px solid rgba(255,255,255,0.1)",
            width: { xs: "100%", md: "auto" },
            boxShadow: "0 0 7px rgba(0,0,0,0.1)",
          }}
        >
          {[
            { id: "portfolioManagement", label: "Portfolio Management", short: "Portfolio" },
            { id: "derivatives", label: "Derivatives", short: "Derivatives" },
          ].map((item) => (
            <Box
              key={item.id}
              onClick={() => setTab(item.id)}
              sx={{
                flex: { xs: 1, md: "unset" },
                px: { xs: 1.25, md: 2 },
                py: 1.25,
                minHeight: 44,
                boxSizing: "border-box",
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: tab === item.id ? "#3E2B03" : "rgba(248,244,234,0.8)",
              }}
            >
              <Text
                variant="caption2"
                sx={{
                  fontWeight: 700,
                  color: tab === item.id ? "#F9FAFB" : "#111827",
                  whiteSpace: "nowrap",
                  fontSize: { xs: 12, md: undefined },
                  display: { xs: "none", sm: "block" },
                }}
              >
                {item.label}
              </Text>
              <Text
                sx={{
                  fontWeight: 700,
                  color: tab === item.id ? "#F9FAFB" : "#111827",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  display: { xs: "block", sm: "none" },
                }}
              >
                {item.short}
              </Text>
            </Box>
          ))}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: { xs: "flex", sm: "grid" },
          gridTemplateColumns: {
            sm:
              gridItemCount === 1
                ? "minmax(0, 301px)"
                : "repeat(auto-fit, minmax(260px, 1fr))",
            lg:
              gridItemCount === 1
                ? "minmax(0, 301px)"
                : `repeat(${Math.min(gridItemCount, 3)}, minmax(0, 1fr))`,
          },
          justifyContent: "start",
          gap: { xs: 1.5, sm: 1.75 },
          alignItems: "stretch",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          mx: { xs: 0, md: 0 },
          px: { xs: 2, md: 0 },
          pb: 0.5,
          minWidth: 0,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },

          "& > *": {
            minHeight: 309,
            flex: { xs: "0 0 auto", sm: "unset" },
            width: {
              xs: "min(301px, 82vw)",
              sm: "100%",
            },
            maxWidth: {
              xs: "min(301px, 82vw)",
              sm: gridItemCount === 1 ? 301 : "100%",
            },
            scrollSnapAlign: { xs: "start", sm: "unset" },
          },
        }}
      >
        {showSkeleton
          ? Array.from({ length: 3 }).map((_, index) => (
              <PrivateWealthCardSkeleton key={`pw-skeleton-${index}`} />
            ))
          : displayProducts.map((product) => (
              <PrivateWealthCard
                key={product.code || product.id}
                product={product}
                onClick={() => {
                  if (product.comingSoon && !product.productInfoPath) return;
                  onProductClick?.(product);
                }}
                onPmsWaitlistClick={() => setIsPmsWaitlistOpen(true)}
                hasPendingCall={hasProductPendingCall(pendingCallData, product)}
                pendingCount={getProductPendingCount(pendingCallData, product.code)}
              />
            ))}
      </Box>

      {/* <PmsWaitlistModal
        open={isPmsWaitlistOpen}
        onClose={() => setIsPmsWaitlistOpen(false)}
      /> */}
    </Box>
  );
};

export default PrivateWealthSection;
